import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { useCallback, useEffect } from "react";
import { MasterUI } from "../../../../components/common/MasterUI";
import { PageMasterMode, usePageContext } from "../../../../components/navigation/PageContext";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { getGlobalQueryLabel, NavigateGlobalQuery } from "../../../../helpers/navigation";
import { useMeetingAccessibility } from "../../../../hooks/meeting";
import { MeetingReplayView } from "../vod/MeetingReplayView";

/**
 * Standalone replay route (`/player/:id`), registered in both the public and
 * private trees so it works logged out. A single request — the public meeting
 * accessibility endpoint — both gates access and (when `canAccess`) carries the
 * VODs to play, so a visitor can watch a public meeting's replays without an
 * account. No access ⇒ no VODs ⇒ a denial screen. Rendering is shared with the
 * /list overlay via MeetingReplayView.
 */
export const MeetingPlayerPage = () => {
  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);

  const id = pageContext?.params?.id ?? "";
  const provider = pageContext?.searchParams.get(getGlobalQueryLabel(NavigateGlobalQuery.PROVIDER)) ?? undefined;

  const accessibility = useMeetingAccessibility(id, { provider });

  // Once the accessibility endpoint has resolved a non-canonical id (shortId
  // or provider-scoped external id), rewrite the URL in place with the
  // meeting's real id and without the consumed `provider` query (`goTo` only
  // carries sticky queries).
  useEffect(() => {
    if (!accessibility?._id) return;
    if (accessibility._id === id && !provider) return;
    pageContextRef.current?.goTo(PageMasterMode.PLAYER, { id: accessibility._id }, { replace: true });
  }, [id, provider, accessibility?._id, pageContextRef]);

  // Back to wherever we came from (the list for a logged-in user, the lobby for
  // a visitor); fall back to this meeting's lobby when opened from a bare link.
  // The lobby fallback needs `provider` to resolve a provider-scoped id.
  const onBack = useCallback(() => {
    pageContextRef.current?.goToPrevious(PageMasterMode.LOBBY, { id }, { forwardParams: [NavigateGlobalQuery.PROVIDER] });
  }, [id]);

  return <MasterUI onBack={onBack}><MeetingReplayView accessibility={accessibility} /></MasterUI>;
};
